import { useState, useCallback, useRef, useEffect } from 'react';
import adminApi from '../services/tenantService';
import {
  Tenant,
  TenantDetails,
  DashboardStats,
  TenantEmployee,
  TenantOperations,
  TenantFinancials,
  TenantApiUsage,
  AuditLog,
} from '../types/tenant';

interface UseAdminDashboardOptions {
  autoLoad?: boolean;
}

interface AdminState {
  tenants: Tenant[];
  total: number;
  stats: DashboardStats | null;
  selectedTenant: TenantDetails | null;
  loading: boolean;
  error: string | null;
  // Tab-specific data
  employees: { data: TenantEmployee[]; total: number } | null;
  operations: TenantOperations | null;
  financials: TenantFinancials | null;
  apiUsage: TenantApiUsage | null;
  auditLogs: AuditLog[];
}

interface AdminActions {
  fetchTenants: (params?: Record<string, unknown>) => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchTenantDetails: (id: number) => Promise<void>;
  fetchTenantEmployees: (id: number) => Promise<void>;
  fetchTenantOperations: (id: number) => Promise<void>;
  fetchTenantFinancials: (id: number) => Promise<void>;
  fetchTenantApiUsage: (id: number, days?: number) => Promise<void>;
  fetchTenantAuditLogs: (id: number) => Promise<void>;
  suspendTenant: (id: number, reason: string) => Promise<void>;
  resumeTenant: (id: number) => Promise<void>;
  cancelTenant: (id: number, reason: string) => Promise<void>;
  changePlan: (id: number, planSlug: string) => Promise<void>;
  impersonateTenant: (id: number) => Promise<{ token: string; bossEmail: string }>;
  manageCredits: (id: number, amount: number, type: 'add' | 'deduct', reason: string) => Promise<void>;
  clearSelectedTenant: () => void;
}

export function useAdminDashboard(options: UseAdminDashboardOptions = {}): AdminState & AdminActions {
  const { autoLoad = true } = options;
  const statsReqIdRef = useRef(0);
  const tenantsReqIdRef = useRef(0);
  const detailsReqIdRef = useRef(0);

  const [state, setState] = useState<AdminState>({
    tenants: [],
    total: 0,
    stats: null,
    selectedTenant: null,
    loading: false,
    error: null,
    employees: null,
    operations: null,
    financials: null,
    apiUsage: null,
    auditLogs: [],
  });

  const fetchStats = useCallback(async () => {
    const reqId = ++statsReqIdRef.current;
    try {
      const stats = await adminApi.getDashboardStats();
      if (reqId === statsReqIdRef.current) {
        setState(prev => ({ ...prev, stats }));
      }
    } catch (err) {
      if (reqId === statsReqIdRef.current) {
        setState(prev => ({ ...prev, error: (err as Error).message }));
      }
    }
  }, []);

  const fetchTenants = useCallback(async (params: Record<string, unknown> = {}) => {
    const reqId = ++tenantsReqIdRef.current;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await adminApi.getTenants(params as Parameters<typeof adminApi.getTenants>[0]);
      if (reqId === tenantsReqIdRef.current) {
        setState(prev => ({ ...prev, tenants: result.data, total: result.total, loading: false }));
      }
    } catch (err) {
      if (reqId === tenantsReqIdRef.current) {
        setState(prev => ({ ...prev, loading: false, error: (err as Error).message }));
      }
    }
  }, []);

  const fetchTenantDetails = useCallback(async (id: number) => {
    const reqId = ++detailsReqIdRef.current;
    try {
      const details = await adminApi.getTenantDetails(id);
      if (reqId === detailsReqIdRef.current) {
        setState(prev => ({ ...prev, selectedTenant: details }));
      }
    } catch (err) {
      if (reqId === detailsReqIdRef.current) {
        setState(prev => ({ ...prev, error: (err as Error).message }));
      }
    }
  }, []);

  const fetchTenantEmployees = useCallback(async (id: number) => {
    try {
      const result = await adminApi.getTenantEmployees(id);
      setState(prev => ({ ...prev, employees: result }));
    } catch (err) {
      setState(prev => ({ ...prev, error: (err as Error).message }));
    }
  }, []);

  const fetchTenantOperations = useCallback(async (id: number) => {
    try {
      const result = await adminApi.getTenantOperations(id);
      setState(prev => ({ ...prev, operations: result }));
    } catch (err) {
      setState(prev => ({ ...prev, error: (err as Error).message }));
    }
  }, []);

  const fetchTenantFinancials = useCallback(async (id: number) => {
    try {
      const result = await adminApi.getTenantFinancials(id);
      setState(prev => ({ ...prev, financials: result }));
    } catch (err) {
      setState(prev => ({ ...prev, error: (err as Error).message }));
    }
  }, []);

  const fetchTenantApiUsage = useCallback(async (id: number, days = 30) => {
    try {
      const result = await adminApi.getTenantApiUsage(id, days);
      setState(prev => ({ ...prev, apiUsage: result }));
    } catch (err) {
      setState(prev => ({ ...prev, error: (err as Error).message }));
    }
  }, []);

  const fetchTenantAuditLogs = useCallback(async (id: number) => {
    try {
      const result = await adminApi.getTenantAuditLogs(id);
      setState(prev => ({ ...prev, auditLogs: result }));
    } catch (err) {
      setState(prev => ({ ...prev, error: (err as Error).message }));
    }
  }, []);

  // Clear tab-specific data so tabs re-fetch after mutations
  const clearTabData = useCallback(() => {
    setState(prev => ({
      ...prev,
      employees: null,
      operations: null,
      financials: null,
      apiUsage: null,
      auditLogs: [],
    }));
  }, []);

  const suspendTenant = useCallback(async (id: number, reason: string) => {
    await adminApi.suspendTenant(id, reason);
    clearTabData();
  }, [clearTabData]);

  const resumeTenant = useCallback(async (id: number) => {
    await adminApi.resumeTenant(id);
    clearTabData();
  }, [clearTabData]);

  const cancelTenant = useCallback(async (id: number, reason: string) => {
    await adminApi.cancelTenant(id, reason);
    clearTabData();
  }, [clearTabData]);

  const changePlan = useCallback(async (id: number, planSlug: string) => {
    await adminApi.changePlan(id, planSlug);
    clearTabData();
  }, [clearTabData]);

  const impersonateTenant = useCallback(async (id: number) => {
    return adminApi.impersonateTenant(id);
  }, []);

  const manageCredits = useCallback(async (id: number, amount: number, type: 'add' | 'deduct', reason: string) => {
    await adminApi.manageCredits(id, amount, type, reason);
    clearTabData();
  }, [clearTabData]);

  const clearSelectedTenant = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedTenant: null,
      employees: null,
      operations: null,
      financials: null,
      apiUsage: null,
      auditLogs: [],
    }));
  }, []);

  useEffect(() => {
    if (autoLoad) {
      fetchStats();
      fetchTenants();
    }
  }, [autoLoad, fetchStats, fetchTenants]);

  return {
    ...state,
    fetchTenants,
    fetchStats,
    fetchTenantDetails,
    fetchTenantEmployees,
    fetchTenantOperations,
    fetchTenantFinancials,
    fetchTenantApiUsage,
    fetchTenantAuditLogs,
    suspendTenant,
    resumeTenant,
    cancelTenant,
    changePlan,
    impersonateTenant,
    manageCredits,
    clearSelectedTenant,
  };
}

export default useAdminDashboard;
